import * as React from 'react';
import { connect } from 'react-redux';
import { get, isEmpty, isNil, sortBy } from 'lodash';

import { withRouter } from 'libs/router';
import PromptExit from 'libs/router/prompt-exit/PromptExit';

import ActionButton from 'components/buttons/action-button/ActionButton';
import ActionLink from 'components/buttons/action-link/ActionLink';
import CheckboxesList from 'components/inputs/checkboxes-list/CheckboxesList';
import ListCreateEditModal from 'modules/lists/components/ListsCreateEditModal';
import ListDashboardFilters from 'modules/lists/components/dashboard-filters/ListDashboardFilters';
import Spinner from 'components/spinner/Spinner';
import { ModalFrameFooter, ModalFrameMessage } from 'components/modal';
import BrandTabsModal from 'components/brands-filter/BrandTabsModal';
import EntityResources from 'components/entity/entity-resources/EntityResources';

import { fetchInfluencerLists, resetInfluencerLists } from 'modules/lists/reducers/influencerLists';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { hasAccessToBrandProfile } from 'utils/organization';
import { BRAND } from 'utils/brands';
import { getEntityFromProfile } from 'utils/view';
import { CUSTOM_LIST_TYPE, LIST_DASHBOARD_SORT_OPTIONS, getQueryValueFromUrl, getSortOptions } from 'utils/list';

const sortByBrandNameValue = LIST_DASHBOARD_SORT_OPTIONS.BRAND_NAME.value;

import type {
  CheckboxesListItem,
  Dispatch,
  InfluencerMeta,
  InfluencerProfile,
  List,
  LocationWindow,
  Organizations
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./generic-lists-modal.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

type OwnProps = {
  modalHeader: string,
  modalSubTitle: string,
  withCollectionCreate: boolean,
  hideCount?: boolean,
  influencerProfile: InfluencerProfile,
  selectedItems: Array<number>,
  influencerMeta: InfluencerMeta,
  params?: {
    listId: string
  },
  onCloseModal: (closeWithoutProcessing?: boolean) => void,
  onListItemClick: (b: CheckboxesListItem, a: List) => void
};

type StateProps = {
  organizations: Organizations,
  isLoading: boolean,
  activeBrandFilter: string,
  location: LocationWindow,
  lists: Array<List>
};

type DispatchProps = {
  dispatchFetchInfluencerLists: (c: number, b: string, a: string) => Dispatch,
  dispatchResetInfluencerLists: () => Dispatch
};

type Props = OwnProps & StateProps & DispatchProps;

type GroupedListItem = {
  id: string,
  brandLists: Array<List>
};

type State = {
  addNew: boolean,
  isModified: boolean,
  groupedLists: Array<GroupedListItem>
};

class GenericListsModal extends React.PureComponent<Props, State> {
  state: State = {
    addNew: false,
    isModified: false,
    groupedLists: []
  };

  componentDidMount() {
    this.fetchLists();
  }

  componentDidUpdate(prevProps, prevState) {
    const { activeBrandFilter, lists, location } = this.props;
    const { addNew } = this.state;

    const sort = getQueryValueFromUrl(location, 'sort');
    const prevSort = getQueryValueFromUrl(prevProps.location, 'sort');

    if (
      activeBrandFilter !== prevProps.activeBrandFilter ||
      sort !== prevSort ||
      !addNew && addNew !== prevState.addNew
    ) {
      this.fetchLists();
    }

    if (lists !== prevProps.lists) {
      this.groupCollection();
    }
  }

  componentWillUnmount() {
    const { dispatchResetInfluencerLists } = this.props;

    dispatchResetInfluencerLists();
  }

  fetchLists() {
    const { activeBrandFilter, organizations, location, dispatchFetchInfluencerLists } = this.props;
    const sortings = [
      LIST_DASHBOARD_SORT_OPTIONS.BRAND_NAME.value,
      LIST_DASHBOARD_SORT_OPTIONS.LIST_NAME_A_Z.value,
      LIST_DASHBOARD_SORT_OPTIONS.MOST_RECENT.value
    ];
    let sort = getQueryValueFromUrl(location, 'sort');

    if (sortings.indexOf(sort) === -1) {
      sort = LIST_DASHBOARD_SORT_OPTIONS.MOST_RECENT.value;
    }

    if (isNil(organizations.currentOrganizationId)) {
      return;
    }

    dispatchFetchInfluencerLists(organizations.currentOrganizationId, activeBrandFilter, sort);
  }

  groupCollection() {
    const { lists } = this.props;

    const groupedLists = lists.reduce((res, list, index, arr) => {
      const previousList = arr[index - 1];
      const entityId: any = get(list, 'brandProfile.id');

      if (get(previousList, 'brandProfile.id') === entityId) {
        const lastBrandItem = res[res.length - 1];
        lastBrandItem.brandLists.push(list);
      } else {
        res.push({ id: entityId, brandLists: [list] });
      }

      return res;
    }, [] as Array<GroupedListItem>);

    this.setState({ groupedLists });
  }

  getListCheckboxesItem(list: List): CheckboxesListItem & { updatedAt?: string } {
    const { organizations, selectedItems } = this.props;
    const isSelected = selectedItems.includes(list.id);

    return ({
      id: list.id,
      label: list.label,
      updatedAt: list.updatedAt,
      entity: getEntityFromProfile(list.brandProfile) ?? undefined,
      isSelected,
      isDisabled: list.brandProfile && !hasAccessToBrandProfile(organizations.list, list.brandProfile.id),
      organizationId: list.organization.id,
      action: {
        label: __t('generic_lists_modal.button_go_to_list'),
        link: `/collections/creators/${list.id}?orgId=${list.organization.id}`,
        hidden: !isSelected
      }
    });
  }

  handleListItemClick = (event: SyntheticEvent<HTMLElement>, item: CheckboxesListItem): void => {
    event.stopPropagation();
    event.preventDefault();

    const { lists, onListItemClick } = this.props;
    const list = lists.find(l => l.id === item.id);

    if (item.isDisabled || !list) {
      return;
    }

    if (onListItemClick) {
      onListItemClick(item, list);
    }
  };

  handleHideNewList = () => {
    this.setState({
      addNew: false
    });
  };

  handleShowNewList = () => {
    this.setState({
      addNew: true
    });
  };

  renderByBrand(otherProps: any): React.ReactNode {
    const { activeBrandFilter, location } = this.props;
    const { groupedLists } = this.state;
    const sort = getQueryValueFromUrl(location, 'sort');

    return (
      <React.Fragment>
        {
          groupedLists.map((item, index) => {
            const brandLists = sort === sortByBrandNameValue
              ? sortBy(item.brandLists, l => l.label.toLowerCase())
              : item.brandLists;
            const brandCheckboxesLists = brandLists.map(list => this.getListCheckboxesItem(list));
            const firstList = brandCheckboxesLists[0];
            const entity = firstList.entity;

            return (
              <EntityResources
                withPaddedChildren
                key={`entity-resource-${index}-${String(entity ? entity.id : '')}`}
                entity={activeBrandFilter === BRAND ? null : entity}
              >
                <CheckboxesList
                  lists={brandCheckboxesLists}
                  {...otherProps}
                />
              </EntityResources>
            );
          })
        }
      </React.Fragment>
    );
  }

  renderSortBy() {
    const { location, activeBrandFilter } = this.props;

    return (
      <div className="add-to-lists-modal__filters">
        <ListDashboardFilters
          withSortOnly
          location={location}
          sortOptions={getSortOptions(CUSTOM_LIST_TYPE, activeBrandFilter)}
        />
      </div>
    );
  }

  renderLabel = (item) => (
    <div className="add-to-lists-modal__label">
      <div className="add-to-lists-modal__name text-overflow-ellipsis">
        {item.label}
      </div>
      <div className="add-to-lists-modal__date">
        {`${__t('generic_lists_modal.modal_list_label_updated')}: ${getDisplayDate(item.updatedAt)}`}
      </div>
    </div>
  );

  renderLists() {
    const { modalSubTitle, lists, selectedItems, params, activeBrandFilter, withCollectionCreate, onCloseModal } = this.props;

    return (
      <React.Fragment>
        <ModalFrameMessage
          size="h3"
          message={`<strong>${modalSubTitle}</strong>`}
        />
        {
          lists.length === 0 ?
            <ModalFrameMessage
              message={__t('generic_lists_modal.actions_empty_list')}
            /> :
            this.renderSortBy()
        }
        {
          withCollectionCreate && activeBrandFilter === BRAND && (
            <div className="add-to-lists-modal__action">
              <ActionLink
                size="inherit"
                color="secondary"
                decoration="none"
                onClick={this.handleShowNewList}
              >
                {__t('generic_lists_modal.button_add_new_list')}
              </ActionLink>
            </div>
          )
        }
        {this.renderByBrand({
          onCheckboxChange: this.handleListItemClick,
          highlightedId: params && Number(params.listId),
          labelComponent: this.renderLabel
        })}
        <ModalFrameFooter>
          <ActionButton
            onClick={() => onCloseModal(isEmpty(lists) || isEmpty(selectedItems))}
          >
            {__t('collabary_common.action_button.done')}
          </ActionButton>
        </ModalFrameFooter>
      </React.Fragment>
    );
  }

  renderNewList() {
    const { organizations } = this.props;

    if (isNil(organizations.currentOrganizationId)) {
      return null;
    }

    return (
      <ListCreateEditModal
        title={__t('generic_lists_modal.modal_title_create')}
        organizationId={organizations.currentOrganizationId}
        onCloseModal={this.handleHideNewList}
      />
    );
  }

  renderExistingLists() {
    const { modalHeader, influencerProfile, isLoading, influencerMeta, hideCount } = this.props;

    return (
      <BrandTabsModal
        title={modalHeader}
        influencerProfile={influencerProfile}
        influencerMeta={influencerMeta}
        hideCount={hideCount}
        metaKey="lists"
      >
        {
          isLoading ?
            <Spinner
              position="inline"
              size="small"
              color="light"
            /> :
            this.renderLists()
        }
      </BrandTabsModal>
    );
  }

  render(): React.ReactNode {
    const { addNew, isModified } = this.state;

    return (
      <section className="add-to-lists-modal">
        {
          isModified &&
          <PromptExit type="modal" />
        }
        {
          addNew
            ? this.renderNewList()
            : this.renderExistingLists()
        }
      </section>
    );
  }
}

const mapStateToProps = (state): StateProps => {
  const { organizations, influencerLists, brandsFilter, router } = state;
  return ({
    organizations,
    location: router.location,
    isLoading: influencerLists.isLoading,
    lists: influencerLists.lists,
    activeBrandFilter: brandsFilter.activeFilter
  });
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchInfluencerLists: (organizationId, activeBrandFilter, sort) =>
    dispatch(fetchInfluencerLists(organizationId, activeBrandFilter, sort)),

  dispatchResetInfluencerLists: () => dispatch(resetInfluencerLists())
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(GenericListsModal));
